import { formatDateTimeForDisplay, parseDateFromValue } from "../FilterBuilderRow/value-utils";
import { getOperatorDisplay } from "../config/schema-utils";
import {
  FilterValueType,
  getFilterDefinitionByKey,
  type StructuredSearchSchema,
} from "../config/structured-search-schema";
import { type Filter } from "../config/types";
import { formatFilterValueByType } from "../config/value-formatter";

const REQUEST_START_TIME_FIELD = "request_start_time";

const formatBetweenDisplayValue = (value: string): string => {
  if (value.includes(" - ")) {
    return value;
  }

  const parts = value
    .split(",")
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0]} - ${parts[1]}`;
  }

  return value;
};

const formatBooleanFilterLabel = (displayName: string, operator: string): string => {
  if (operator === "is_false") {
    return displayName.replace(" is ", " is not ");
  }

  return displayName;
};

/**
 * Fallback formatting for filters not present in the schema's filter list (e.g.
 * the global date-range control compiled to `request_start_time`). Schema-backed
 * filters are formatted by their {@link FilterValueType} via the shared formatter.
 */
const formatUnknownFilterValue = (filter: Filter): string => {
  const { operator, value } = filter;

  if (value === true || value === false) {
    return "";
  }

  if (Array.isArray(value)) {
    if (filter.field === REQUEST_START_TIME_FIELD && value.length >= 2) {
      const start = parseDateFromValue(String(value[0] ?? ""));
      const end = parseDateFromValue(String(value[1] ?? ""));
      if (start && end) {
        return `${formatDateTimeForDisplay(start)} - ${formatDateTimeForDisplay(end)}`;
      }
    }
    if (value.length === 2 && value.every(item => typeof item === "number")) {
      return `${value[0]} - ${value[1]}`;
    }
    if (operator === "in" || operator === "not_in") {
      const labels =
        filter.multiValueDisplay && filter.multiValueDisplay.length > 0
          ? filter.multiValueDisplay
          : value.map(item => String(item).trim()).filter(Boolean);
      const joined = labels.join(", ");
      return joined ? `(${joined})` : "";
    }
    return value.join(", ");
  }

  if (typeof value === "object" && value !== null && "amount" in value) {
    return `${value.amount} ${value.unit}`;
  }

  if (filter.field === REQUEST_START_TIME_FIELD && typeof value === "string") {
    const parsed = parseDateFromValue(value);
    if (parsed) {
      return formatDateTimeForDisplay(parsed);
    }
  }

  return String(value);
};

const formatFilterChipValue = (filter: Filter, schema: StructuredSearchSchema): string => {
  if (filter.valueDisplay) {
    return filter.operator === "between"
      ? formatBetweenDisplayValue(filter.valueDisplay)
      : filter.valueDisplay;
  }

  const definition = getFilterDefinitionByKey(schema, filter.field);
  if (!definition) {
    return formatUnknownFilterValue(filter);
  }

  return formatFilterValueByType({
    type: definition.type,
    value: filter.value,
    operator: filter.operator,
    options: definition.options,
    multiValueDisplay: filter.multiValueDisplay,
  });
};

export const getFilterChipLabel = (filter: Filter, schema: StructuredSearchSchema): string => {
  const displayName = getFilterDefinitionByKey(schema, filter.field)?.label ?? filter.field;
  if (filter.type === FilterValueType.Boolean) {
    return formatBooleanFilterLabel(displayName, filter.operator);
  }

  const operatorLabel = getOperatorDisplay(schema, {
    operator: filter.operator,
    field: filter.field,
    nestedKey: filter.nestedKey,
  });

  const nestedKeyLabel = filter.nestedKeyDisplay ?? filter.nestedKey;
  const fieldLabel = nestedKeyLabel ? `${displayName} [${nestedKeyLabel}]` : displayName;

  const labelParts = [fieldLabel, operatorLabel, formatFilterChipValue(filter, schema)];

  return labelParts.filter(Boolean).join(" ");
};
